import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, LinearProgress, Chip, Divider,
    IconButton, TextField, Alert, CircularProgress, Tooltip,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
    RocketLaunch as RocketIcon,
    Close as CloseIcon,
    CardGiftcard as TokenIcon,
    WhatsApp as WhatsAppIcon,
    Upgrade as UpgradeIcon,
    TrendingUp as TrendingIcon,
    DeleteOutline as DeleteIcon,
    Storefront as BrandIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

// Evento global disparado pelo interceptor do Axios quando recebe 429 em /articles
export const PLAN_LIMIT_EVENT = 'plan-limit-reached';

const PlanLimitDialog = () => {
    const [open, setOpen] = useState(false);
    const [planInfo, setPlanInfo] = useState(null);
    const [whatsappNumber, setWhatsappNumber] = useState('5511980464551');

    // Token de upgrade
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const { updateUser } = useAuth();

    // Escuta o evento global
    useEffect(() => {
        const handler = async (e) => {
            setOpen(true);
            setToken('');
            setError('');
            setSuccess('');
            // Buscar detalhes do plano frescos ao abrir
            try {
                const [statusRes, configRes] = await Promise.all([
                    api.get('/auth/activation-status'),
                    api.get('/system-config/public/contact')
                ]);
                if (statusRes.data.success) setPlanInfo(statusRes.data.data);
                const wa = configRes.data?.data?.whatsapp;
                if (wa) setWhatsappNumber(wa);
            } catch {
                // fallback: usa o detail do evento se existir
                if (e.detail) setPlanInfo(e.detail);
            }
        };
        window.addEventListener(PLAN_LIMIT_EVENT, handler);
        return () => window.removeEventListener(PLAN_LIMIT_EVENT, handler);
    }, []);

    const handleTokenChange = (e) => {
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const groups = raw.match(/.{1,4}/g) || [];
        setToken(groups.join('-').slice(0, 19));
        setError('');
    };

    const handleRedeem = async () => {
        if (token.length < 19) {
            setError('Token incompleto. Formato: XXXX-XXXX-XXXX-XXXX');
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/mia/activate', { token });
            if (res.data.success) {
                setSuccess('Token resgatado! Suas publicações foram renovadas.');
                if (res.data.user && updateUser) updateUser(res.data.user);
                // Fechar após 2s e recarregar status
                setTimeout(() => {
                    setOpen(false);
                    window.dispatchEvent(new CustomEvent('refresh-usage-counter'));
                }, 2000);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || 'Token inválido ou já utilizado.');
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsApp = () => {
        const planName = planInfo?.plan?.name || 'atual';
        const used = planInfo?.usage?.monthlyLinks ?? '?';
        const max = planInfo?.plan?.maxLinks ?? '?';
        const msg = encodeURIComponent(
            `Olá! Atingi o limite mensal do meu plano ${planName} (${used}/${max} publicações) e gostaria de saber sobre upgrade ou créditos adicionais.`
        );
        window.open(`https://wa.me/${whatsappNumber}?text=${msg}`, '_blank');
    };

    const used = planInfo?.usage?.monthlyLinks ?? 0;
    const max = planInfo?.plan?.maxLinks ?? 0;
    const planName = planInfo?.plan?.name ?? 'Atual';
    const pct = max > 0 ? Math.round((used / max) * 100) : 100;
    const deletedLinks = planInfo?.usage?.deletedLinks ?? 0;
    const deletedLinksByBrand = planInfo?.usage?.deletedLinksByBrand ?? [];
    const brandsUsage = planInfo?.usage?.brandsUsage ?? [];

    const upgradePlans = [
        { name: 'Pro', pub: 300, brands: 3, price: 'R$797/mês' },
        { name: 'Business', pub: 700, brands: 7, price: 'R$1.397/mês' },
        { name: 'Enterprise', pub: 2000, brands: 15, price: 'R$2.197/mês' },
    ].filter(p => p.name !== planName);

    return (
        <Dialog
            open={open}
            onClose={() => !loading && setOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header gradiente */}
            <Box sx={{
                background: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)',
                px: 3, py: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <RocketIcon sx={{ color: '#fff', fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#fff" lineHeight={1.1}>
                            Limite mensal atingido
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            Plano {planName} · {used}/{max} publicações este mês
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
                {/* Barra de progresso */}
                <Box mb={3}>
                    <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                            height: 10, borderRadius: 5,
                            bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
                                borderRadius: 5
                            }
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" mt={0.5} display="block" textAlign="right">
                        {pct}% do limite utilizado
                    </Typography>
                </Box>

                {/* Detalhamento por Marca - Accordion */}
                {(brandsUsage.length > 0 || deletedLinks > 0 || deletedLinksByBrand.length > 0) && (
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 2.5,
                            borderRadius: 2,
                            border: '1px solid rgba(96,165,250,0.25)',
                            bgcolor: 'rgba(96,165,250,0.04)',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { mb: 2.5 }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: '#60a5fa' }} />}
                            sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { my: 0.75 }, px: 2 }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <BrandIcon sx={{ color: '#60a5fa', fontSize: 18 }} />
                                <Typography variant="body2" fontWeight={700} color="#60a5fa">
                                    Consumo por marca
                                </Typography>
                                <Chip
                                    label={`${brandsUsage.reduce((s, b) => s + b.count, 0)} pub este mês`}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: 'rgba(96,165,250,0.15)', color: '#93c5fd', ml: 0.5 }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                            {brandsUsage.map(b => (
                                <Box key={b.brandId} display="flex" alignItems="center" justifyContent="space-between" py={0.4}>
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>
                                        {b.brandName}
                                    </Typography>
                                    <Chip
                                        label={`${b.count} pub`}
                                        size="small"
                                        sx={{ bgcolor: 'rgba(96,165,250,0.15)', color: '#93c5fd', fontWeight: 700, fontSize: '0.7rem' }}
                                    />
                                </Box>
                            ))}
                            {/* Excluídos por marca */}
                            {deletedLinksByBrand.length > 0 && (
                                <Box mt={brandsUsage.length > 0 ? 1.5 : 0}>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                        <DeleteIcon sx={{ fontSize: 13, color: '#f87171' }} />
                                        <Typography variant="caption" color="#f87171" fontWeight={600}>
                                            Excluídos por marca
                                        </Typography>
                                    </Box>
                                    {deletedLinksByBrand.map((b, i) => (
                                        <Box key={b.brandId || i} display="flex" alignItems="center" justifyContent="space-between" py={0.3}>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '65%' }}>
                                                {b.brandName}
                                            </Typography>
                                            <Chip
                                                label={`${b.count} art.`}
                                                size="small"
                                                sx={{ bgcolor: 'rgba(248,113,113,0.1)', color: '#f87171', fontWeight: 700, fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {/* Fallback: total sem detalhamento */}
                            {deletedLinks > 0 && deletedLinksByBrand.length === 0 && (
                                <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <DeleteIcon sx={{ fontSize: 14, color: '#f87171' }} />
                                        <Typography variant="body2" color="#f87171">Excluídos (total)</Typography>
                                    </Box>
                                    <Chip label={`${deletedLinks} art.`} size="small" sx={{ bgcolor: 'rgba(248,113,113,0.12)', color: '#f87171', fontWeight: 700, fontSize: '0.7rem' }} />
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Opção 1: Resgatar token */}
                <Box sx={{
                    p: 2, mb: 2, borderRadius: 2,
                    border: '1px solid rgba(99, 102, 241, 0.4)',
                    bgcolor: 'rgba(99, 102, 241, 0.08)'
                }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <TokenIcon sx={{ color: '#818cf8', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={700} color="#818cf8">
                            Tenho um token de crédito
                        </Typography>
                    </Box>

                    {success ? (
                        <Alert severity="success" sx={{ mb: 0 }}>{success}</Alert>
                    ) : (
                        <>
                            {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}
                            <Box display="flex" gap={1}>
                                <TextField
                                    size="small"
                                    placeholder="XXXX-XXXX-XXXX-XXXX"
                                    value={token}
                                    onChange={handleTokenChange}
                                    inputProps={{ maxLength: 19, style: { fontFamily: 'monospace', letterSpacing: '0.1em' } }}
                                    sx={{ flex: 1 }}
                                    disabled={loading}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleRedeem}
                                    disabled={loading || token.length < 19}
                                    sx={{
                                        background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                        whiteSpace: 'nowrap',
                                        minWidth: 100
                                    }}
                                >
                                    {loading ? <CircularProgress size={18} color="inherit" /> : 'Resgatar'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Divider */}
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">ou</Typography>
                    <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Opção 2: Upgrade de plano */}
                <Box mb={2.5}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <TrendingIcon sx={{ color: '#34d399', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={700} color="#34d399">
                            Fazer upgrade de plano
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {upgradePlans.map(p => (
                            <Box
                                key={p.name}
                                sx={{
                                    flex: '1 1 120px',
                                    p: 1.5, borderRadius: 2, textAlign: 'center',
                                    border: '1px solid rgba(52, 211, 153, 0.25)',
                                    bgcolor: 'rgba(52, 211, 153, 0.06)',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s, border-color 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(52, 211, 153, 0.12)',
                                        borderColor: 'rgba(52, 211, 153, 0.5)'
                                    }
                                }}
                                onClick={handleWhatsApp}
                            >
                                <Typography variant="caption" fontWeight={800} color="#34d399">{p.name}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {p.pub} pub/mês
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="text.primary" display="block">
                                    {p.price}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <Button
                    onClick={() => setOpen(false)}
                    variant="text"
                    sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                >
                    Fechar
                </Button>
                <Button
                    onClick={handleWhatsApp}
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                        flex: 2,
                        background: 'linear-gradient(90deg, #25d366, #128c7e)',
                        fontWeight: 700
                    }}
                >
                    Falar com Mensure
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlanLimitDialog;
